import { useEffect, useRef } from 'react';
import './SoundWaveVisualizer.css';
import { setupAudioContext } from './AudioContextSingleton';

function SoundWaveVisualizer({ audioRef, isPlaying, isEnded = false, barCount = 24, color }) {
  const barRefs = useRef([]);
  const analyserRef = useRef(null);
  const audioContextRef = useRef(null);
  const dataArrayRef = useRef(null);
  const animationIdRef = useRef(null);
  const isAnimatingRef = useRef(false);

  const smoothHeight = (cur, tgt) => cur + (tgt - cur) * 0.3;

  const resetBars = () => {
    barRefs.current.forEach(bar => {
      if (bar) bar.style.height = '0px';
    });
  };

  const draw = () => {
    if (!isAnimatingRef.current || !analyserRef.current) return;

    if (isPlaying) {
      analyserRef.current.getByteFrequencyData(dataArrayRef.current);
      const samplesPerBar = Math.floor(dataArrayRef.current.length / barCount);

      barRefs.current.forEach((bar, i) => {
        if (!bar) return;

        const chunk = dataArrayRef.current.slice(
          i * samplesPerBar,
          i === barCount - 1 ? dataArrayRef.current.length : (i + 1) * samplesPerBar
        );
        const avg = chunk.reduce((a, v) => a + v, 0) / chunk.length;
        const target = isEnded ? 0 : (avg / 255) * 40;

        const current = parseFloat(bar.style.height) || 0;
        bar.style.height = `${smoothHeight(current, target)}px`;
      });
    }

    animationIdRef.current = requestAnimationFrame(draw);
  };

  useEffect(() => {
    const audioEl = audioRef.current;
    if (!audioEl) return;

    const { audioContext, analyser, dataArray } = setupAudioContext(audioEl);

    audioContextRef.current = audioContext;
    analyserRef.current = analyser;
    dataArrayRef.current = dataArray;

    return () => {
      cancelAnimationFrame(animationIdRef.current);
      isAnimatingRef.current = false;
      resetBars();
      // Don't close audioContext here because it's a singleton shared resource
    };
  }, [audioRef]);

  useEffect(() => {
    if (!audioContextRef.current || !analyserRef.current) return;

    if (isPlaying || (!isPlaying && !isEnded)) {
      if (audioContextRef.current.state === 'suspended') {
        audioContextRef.current.resume();
      }
      isAnimatingRef.current = true;
      draw();
    } else {
      isAnimatingRef.current = false;
      cancelAnimationFrame(animationIdRef.current);
      resetBars();
    }

    return () => {
      cancelAnimationFrame(animationIdRef.current);
      isAnimatingRef.current = false;
    };
  }, [isPlaying, isEnded]);

  useEffect(() => {
    return () => {
      barRefs.current = [];
    };
  }, []);

  return (
    <div className="sound-wave-bars">
      {Array.from({ length: barCount }, (_, i) => (
        <div
          key={i}
          ref={(el) => (barRefs.current[i] = el)}
          className="bar"
          style={{ backgroundColor: color }}
        />
      ))}
    </div>
  );
}

export default SoundWaveVisualizer;
